import React, { useCallback, useEffect, createContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMyProfile } from "../network/network";
import { handleApiError } from "../utils/errorHandler";

export const AppContext = createContext();

const AppContextProvider = ({ children }) => {
  const { t } = useTranslation();
  const [authUser, setAuthUser] = useState(false);
  const [displayWebView, setDisplayWebView] = useState("");
  const [fullPageLoading, setFullPageLoading] = useState(true);
  const [appConfig, setAppConfig] = useState({});
  const [fullScreenPOS, setFullScreenPOS] = useState(false);
  const [isSiderCollapsed, setIsSiderCollapsed] = useState(false);

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      setFullPageLoading(false);
    }, 1500);

    return () => {
      clearTimeout(loadingTimeout);
    };
  }, []);

  const refreshAuthUser = async () => {
    try {
      const response = await getMyProfile();

      if (response?.data?.success === false) {
        handleApiError(response, t);
        return null;
      }

      const user = response?.data?.data?.user || null;
      setAuthUser(user);
      return user;
    } catch (error) {
      handleApiError(error, t);
      return null;
    }
  };

  const setAppConfigWrapper = useCallback((obj = {}) => {
    const nextConfig = Object.prototype.hasOwnProperty.call(obj, "direction")
      ? { ...obj }
      : { ...obj, direction: "ltr" };

    setAppConfig((currentConfig) => {
      const mergedConfig = { ...currentConfig, ...nextConfig };

      return JSON.stringify(currentConfig) === JSON.stringify(mergedConfig)
        ? currentConfig
        : mergedConfig;
    });

    if (nextConfig.direction === "rtl") {
      document.body.classList.add("app_rtl");
      document.body.classList.remove("app_ltr");
    } else if (nextConfig.direction === "ltr") {
      document.body.classList.add("app_ltr");
      document.body.classList.remove("app_rtl");
    }
  }, []);

  const roundOff = useCallback(
    (number) => {
      let places = 8;
      if (authUser.round_off === true) {
        places = 2;
      }

      return +(Math.round(number + "e+" + places) + "e-" + places);
    },
    [authUser],
  );

  return (
    <AppContext.Provider
      value={{
        authUser,
        setAuthUser,
        fullPageLoading,
        setFullPageLoading,
        appConfig,
        setAppConfigWrapper,
        roundOff,
        fullScreenPOS,
        setFullScreenPOS,
        displayWebView,
        setDisplayWebView,
        isSiderCollapsed,
        setIsSiderCollapsed,
        refreshAuthUser,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
